import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import axios from 'axios';
import { PencilSquare, Trash, Plus, Grid3x3Gap, Journal, GearFill, List as MenuIcon, Copy, CalendarWeek } from 'react-bootstrap-icons';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { useWeekNav } from '../contexts/WeekNavContext';
import { getCurrentWeek, getWeekDates, toISO, formatDate } from '../utils/dateUtils';
import { SUBJECT_STYLES, getSubjectStyle } from '../utils/subjectStyles';
import { getClassStyle } from '../utils/classStyles';
import ScheduleEditor from '../components/ScheduleEditor';

function SubjectChip({ subjectId, subjects }) {
  const s    = getSubjectStyle(subjectId, subjects);
  const name = subjects.find(sub => sub.id === subjectId)?.name || 'Allgemein';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 9px', borderRadius: 100,
      fontSize: 11, fontWeight: 600, background: s.tint, color: s.ink, whiteSpace: 'nowrap',
    }}>
      {name}
    </span>
  );
}

const DAYS_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];

function initials(name) {
  if (!name) return '??';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Sub-views ────────────────────────────────────────────────────────────────

function ClassBadge({ classId, classes }) {
  const st = getClassStyle(classId, classes);
  if (!st) return null;
  return (
    <span className="mono" style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 9.5, fontWeight: 700, color: st.ink, background: st.tint,
      border: `1px solid color-mix(in srgb, ${st.ink} 35%, transparent)`,
      borderRadius: 100, padding: '1px 7px', whiteSpace: 'nowrap',
    }}>
      {st.name}
    </span>
  );
}

function WochenrasterView({ weekLessons, weekDates, subjects, classes, maxLessons, lessonTimes, ghosts = [], onEditLesson, onCreateLesson, onMoveLesson }) {
  const slots = Array.from({ length: maxLessons }, (_, i) => i);
  const [draggedLesson, setDraggedLesson] = useState(null);
  const [dragOver,      setDragOver]      = useState(null);

  // Ghosts (recurring-schedule preview) only show when the week has no real lessons.
  const showGhosts = weekLessons.length === 0 && ghosts.length > 0;

  // True when a double-height lesson (or ghost) from row si-1 covers this cell
  function isCovered(di, si) {
    if (si === 0) return false;
    if (weekLessons.some(l => l.dayIndex === di && l.slotIndex === si - 1 && (l.duration || 1) >= 2)) return true;
    if (showGhosts && ghosts.some(g => g.dayIndex === di && g.slotIndex === si - 1 && (g.duration || 1) >= 2)) return true;
    return false;
  }

  return (
    <>
      {weekLessons.length === 0 && (
        <div style={{ padding: showGhosts ? '0 0 18px' : '48px 0', textAlign: 'center', color: 'var(--ink-3)' }}>
          {showGhosts
            ? 'Stundenplan-Vorschau — klicke einen Eintrag an, um daraus eine Lektion zu erstellen.'
            : 'Keine Lektionen diese Woche — klicke auf «+» um eine Lektion hinzuzufügen.'}
        </div>
      )}
    <div className="card-flat scroll-x" style={{ padding: 0, overflow: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(5, 1fr)', minWidth: 480 }}>
        {/* Header — explicit row 1 */}
        <div style={{ gridRow: 1, gridColumn: 1, borderBottom: '1px solid var(--line)' }} />
        {DAYS_LONG.map((d, i) => (
          <div key={d} style={{ gridRow: 1, gridColumn: i + 2, padding: '10px 12px', borderBottom: '1px solid var(--line)', borderLeft: '1px solid var(--line-soft)' }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{formatDate(weekDates[i])}</div>
            <div style={{ fontWeight: 600, fontSize: 13, marginTop: 1 }}>{d}</div>
          </div>
        ))}
        {/* Body — explicit gridRow per slot */}
        {slots.map((si) => (
          <React.Fragment key={si}>
            <div className="mono" style={{
              gridRow: si + 2, gridColumn: 1,
              fontSize: 9, color: 'var(--ink-3)', padding: '6px 4px',
              borderBottom: si < slots.length - 1 ? '1px solid var(--line-soft)' : 'none',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
            }}>
              {lessonTimes[si]
                ? <><span>{lessonTimes[si].start}</span><span style={{ color: 'var(--ink-4)' }}>{lessonTimes[si].end}</span></>
                : <span>{si + 1}</span>}
            </div>
            {Array.from({ length: 5 }, (_, di) => {
              if (isCovered(di, si)) return null;
              const lesson   = weekLessons.find(l => l.dayIndex === di && l.slotIndex === si);
              const ghost    = !lesson && showGhosts ? ghosts.find(g => g.dayIndex === di && g.slotIndex === si) : null;
              const duration = lesson?.duration || ghost?.duration || 1;
              const isOver   = dragOver?.di === di && dragOver?.si === si;
              const isDragged = draggedLesson?.id === lesson?.id;
              return (
                <div
                  key={di}
                  onDragOver={e => { e.preventDefault(); setDragOver({ di, si }); }}
                  onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); }}
                  onDrop={e => {
                    e.preventDefault();
                    if (draggedLesson && !(draggedLesson.dayIndex === di && draggedLesson.slotIndex === si)) {
                      onMoveLesson(draggedLesson, di, si);
                    }
                    setDragOver(null);
                  }}
                  style={{
                    gridRow: duration >= 2 ? `${si + 2} / span 2` : si + 2,
                    gridColumn: di + 2,
                    padding: 6, minHeight: 72,
                    borderLeft: '1px solid var(--line-soft)',
                    borderBottom: (si + duration - 1) < slots.length - 1 ? '1px solid var(--line-soft)' : 'none',
                    background: isOver ? 'var(--accent-muted)' : undefined,
                    outline: isOver ? '2px dashed var(--accent)' : undefined,
                    outlineOffset: -2,
                    transition: 'background 0.1s',
                  }}
                >
                  {lesson ? (() => {
                    const s = getSubjectStyle(lesson.subjectId, subjects);
                    return (
                      <div
                        draggable
                        onDragStart={() => setDraggedLesson(lesson)}
                        onDragEnd={() => { setDraggedLesson(null); setDragOver(null); }}
                        onClick={() => !draggedLesson && onEditLesson(lesson)}
                        style={{
                          background: s.tint, padding: '6px 8px', borderRadius: 6,
                          borderLeft: `3px solid ${s.ink}`, height: '100%',
                          cursor: 'grab', transition: 'opacity 0.15s',
                          opacity: isDragged ? 0.4 : 1,
                          position: 'relative',
                        }}
                      >
                        <div className="mono" style={{ fontSize: 9, fontWeight: 700, color: s.ink, marginBottom: 2 }}>
                          {s.short}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ink-1)', lineHeight: 1.3 }}>{lesson.topic}</div>
                        {lesson.classId && (
                          <div style={{ marginTop: 4 }}>
                            <ClassBadge classId={lesson.classId} classes={classes} />
                          </div>
                        )}
                      </div>
                    );
                  })() : ghost ? (() => {
                    const s = getSubjectStyle(ghost.subjectId, subjects);
                    return (
                      <div
                        onClick={() => onCreateLesson(weekDates[di], si, { subjectId: ghost.subjectId, classId: ghost.classId, duration: ghost.duration || 1 })}
                        title="Lektion aus Stundenplan erstellen"
                        style={{
                          background: 'transparent', padding: '6px 8px', borderRadius: 6,
                          border: `1px dashed color-mix(in srgb, ${s.ink} 55%, transparent)`,
                          borderLeft: `3px solid color-mix(in srgb, ${s.ink} 55%, transparent)`,
                          height: '100%', cursor: 'pointer', opacity: 0.75,
                        }}
                      >
                        <div className="mono" style={{ fontSize: 9, fontWeight: 700, color: s.ink, marginBottom: 4 }}>
                          {s.short}
                        </div>
                        {ghost.classId && <ClassBadge classId={ghost.classId} classes={classes} />}
                      </div>
                    );
                  })() : (
                    <button
                      onClick={() => onCreateLesson(weekDates[di], si)}
                      style={{
                        width: '100%', height: '100%', minHeight: 56,
                        border: '1px dashed var(--line-soft)', background: 'transparent',
                        borderRadius: 6, color: 'var(--ink-4)', cursor: 'pointer', fontSize: 16,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Plus size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
    </>
  );
}

function LektionenView({ allLessons, weekLessons, subjects, classes, lessonTimes, onEdit, onDelete }) {
  const lessons = weekLessons.length > 0 ? weekLessons : allLessons.slice(0, 20);
  if (lessons.length === 0) {
    return <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)' }}>Keine Lektionen vorhanden.</div>;
  }
  return (
    <div className="card-flat scroll-x" style={{ padding: 0, overflow: 'auto' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '110px 110px 1fr 220px 80px',
        padding: '10px 18px', borderBottom: '1px solid var(--line)', background: 'var(--surface)',
        minWidth: 580,
      }}>
        {['Tag', 'Zeit', 'Thema', 'Material', ''].map((h, i) => (
          <div key={i} className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
        ))}
      </div>
      {lessons.map((l, i) => (
        <div key={l.id} style={{
          display: 'grid', gridTemplateColumns: '110px 110px 1fr 220px 80px',
          padding: '12px 18px', alignItems: 'center',
          borderBottom: i < lessons.length - 1 ? '1px solid var(--line-soft)' : 'none',
          minWidth: 580,
        }}>
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{l.dayIndex !== undefined ? DAYS_LONG[l.dayIndex] : formatDate(new Date(l.date + 'T12:00:00'))}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {(() => {
              const si  = l.slotIndex ?? 0;
              const dur = l.duration || 1;
              const t0  = lessonTimes[si];
              const t1  = lessonTimes[si + dur - 1];
              return t0 ? `${t0.start} – ${(t1 || t0).end}` : `${si + 1}${dur >= 2 ? `–${si + 2}` : ''}`;
            })()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SubjectChip subjectId={l.subjectId} subjects={subjects} />
            <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink-1)' }}>{l.topic}</div>
            {l.classId && <ClassBadge classId={l.classId} classes={classes} />}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {l.materials ? l.materials.replace(/\*+/g, '').split('\n')[0].slice(0, 50) : '—'}
          </div>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <button className="btn-icon" onClick={() => onEdit(l)} title="Bearbeiten"><PencilSquare size={13} /></button>
            <button className="btn-icon" onClick={() => onDelete(l.id)} title="Löschen"><Trash size={13} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function TeacherDashboardB() {
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [route,         setRoute]         = useState('woche');
  const [subjects,      setSubjects]      = useState([]);
  const [classes,       setClasses]       = useState([]);
  const { selectedWeek, setSelectedWeek } = useWeekNav();
  const [weekLessons,   setWeekLessons]   = useState([]);
  const [allLessons,    setAllLessons]    = useState([]);

  const [maxLessons, setMaxLessons] = useState(9);
  const [lessonTimes, setLessonTimes] = useState([]);

  // Lesson modal
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingLesson,   setEditingLesson]   = useState(null);
  const [lessonForm,      setLessonForm]      = useState({ subjectId: 'math', topic: '', materials: '', duration: 1, classId: '' });
  const [lessonDate,      setLessonDate]      = useState(new Date().toLocaleDateString('sv'));
  const [lessonSlotIndex, setLessonSlotIndex] = useState(null);

  // Week copy
  const [showCopyModal,   setShowCopyModal]   = useState(false);
  const [copyTargetWeek,  setCopyTargetWeek]  = useState(1);

  // Lesson templates
  const [templates,      setTemplates]      = useState([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName,   setTemplateName]   = useState('');

  // Recurring schedule (own entries) — used for empty-week ghosts
  const [scheduleEntries, setScheduleEntries] = useState([]);

  useEffect(() => {
    fetchSubjects(); fetchClasses(); fetchAllLessons(); fetchTemplates(); fetchSchedule();
    axios.get('/api/settings').then(r => {
      setLessonTimes(r.data.lessonTimes || []);
      setMaxLessons(r.data.maxLessons || 9);
    });
  }, []);
  useEffect(() => {
    fetchWeekLessons(selectedWeek);
  }, [selectedWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  const token   = localStorage.getItem('token');
  const authHdr = { Authorization: `Bearer ${token}` };

  async function fetchSubjects() {
    try { const r = await axios.get('/api/admin/subjects', { headers: authHdr }); setSubjects(r.data); } catch (e) { console.error(e); }
  }
  async function fetchClasses() {
    try { const r = await axios.get('/api/admin/classes'); setClasses([...r.data].sort((a, b) => a.name.localeCompare(b.name, 'de'))); } catch (e) { console.error(e); }
  }
  async function fetchAllLessons() {
    try { const r = await axios.get('/api/lessons'); setAllLessons(r.data); } catch (e) { setAllLessons([]); }
  }
  async function fetchWeekLessons(week) {
    try {
      const r = await axios.get('/api/lessons');
      const dates = getWeekDates(week).map(toISO);
      const raw = r.data.filter(l => dates.includes(l.date));
      const byDay = {};
      raw.forEach(l => { (byDay[l.date] = byDay[l.date] || []).push(l); });
      const mapped = [];
      for (const dateKey of Object.keys(byDay)) {
        const dayLessons = byDay[dateKey];
        const occupied = new Set(dayLessons.filter(l => l.slotIndex !== undefined).map(l => l.slotIndex));
        const legacy = dayLessons
          .filter(l => l.slotIndex === undefined)
          .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
        let next = 0;
        for (const l of legacy) {
          while (occupied.has(next)) next++;
          mapped.push({ ...l, dayIndex: dates.indexOf(dateKey), slotIndex: next });
          occupied.add(next++);
        }
        for (const l of dayLessons.filter(l => l.slotIndex !== undefined)) {
          mapped.push({ ...l, dayIndex: dates.indexOf(dateKey) });
        }
      }
      setWeekLessons(mapped);
    } catch (e) { setWeekLessons([]); }
  }

  async function fetchTemplates() {
    try { const r = await axios.get('/api/templates'); setTemplates(r.data); }
    catch (e) { console.error(e); }
  }
  async function fetchSchedule() {
    try {
      const me = JSON.parse(localStorage.getItem('user') || 'null');
      const r = await axios.get('/api/schedule');
      setScheduleEntries(r.data.filter(e => e.teacherId === me?.id));
    } catch (e) { setScheduleEntries([]); }
  }
  // ── Lesson CRUD ─────────────────────────────────────────────────────────────
  function openCreateLesson(date, slotIndex = null, prefill = null) {
    setEditingLesson(null);
    setLessonDate(toISO(date));
    setLessonSlotIndex(slotIndex);
    setLessonForm({
      subjectId: prefill?.subjectId || subjects[0]?.id || 'math',
      topic: '', materials: '',
      duration: prefill?.duration || 1,
      classId: prefill?.classId || '',
    });
    setShowLessonModal(true);
  }
  function openEditLesson(lesson) {
    setEditingLesson(lesson);
    setLessonDate(lesson.date);
    setLessonForm({ subjectId: lesson.subjectId, topic: lesson.topic, materials: lesson.materials, duration: lesson.duration || 1, classId: lesson.classId || '' });
    setShowLessonModal(true);
  }
  async function handleSaveLesson() {
    if (!lessonForm.topic.trim()) return;
    try {
      if (editingLesson) await axios.put(`/api/lessons/${editingLesson.id}`, lessonForm, { headers: authHdr });
      else               await axios.post('/api/lessons', { ...lessonForm, date: lessonDate, ...(lessonSlotIndex != null ? { slotIndex: lessonSlotIndex } : {}) }, { headers: authHdr });
      if (saveAsTemplate && templateName.trim()) {
        await axios.post('/api/templates', {
          name: templateName.trim(),
          subjectId: lessonForm.subjectId, topic: lessonForm.topic, materials: lessonForm.materials || '',
        });
        setSaveAsTemplate(false); setTemplateName('');
        fetchTemplates();
      }
      setShowLessonModal(false); setEditingLesson(null);
      fetchWeekLessons(selectedWeek); fetchAllLessons();
    } catch (e) { console.error(e); }
  }
  async function handleDeleteLesson(id) {
    if (!window.confirm('Lektion löschen?')) return;
    try {
      await axios.delete(`/api/lessons/${id}`, { headers: authHdr });
      fetchWeekLessons(selectedWeek); fetchAllLessons();
    } catch (e) { console.error(e); }
  }
  async function handleMoveLesson(draggedLesson, targetDi, targetSi) {
    const targetDate   = toISO(weekDates[targetDi]);
    const targetLesson = weekLessons.find(l => l.dayIndex === targetDi && l.slotIndex === targetSi);
    const updates = [
      axios.put(`/api/lessons/${draggedLesson.id}`,
        { subjectId: draggedLesson.subjectId, topic: draggedLesson.topic,
          materials: draggedLesson.materials, date: targetDate, slotIndex: targetSi,
          duration: draggedLesson.duration || 1 },
        { headers: authHdr }),
    ];
    if (targetLesson) {
      updates.push(axios.put(`/api/lessons/${targetLesson.id}`,
        { subjectId: targetLesson.subjectId, topic: targetLesson.topic,
          materials: targetLesson.materials,
          date: toISO(weekDates[draggedLesson.dayIndex]), slotIndex: draggedLesson.slotIndex,
          duration: targetLesson.duration || 1 },
        { headers: authHdr }));
    }
    await Promise.all(updates);
    fetchWeekLessons(selectedWeek);
  }

  async function handleCopyWeek() {
    if (copyTargetWeek === selectedWeek) return;
    const srcDates = getWeekDates(selectedWeek).map(toISO);
    const tgtDates = getWeekDates(copyTargetWeek).map(toISO);
    const srcLessons = allLessons.filter(l => srcDates.includes(l.date));
    for (const l of srcLessons) {
      const di = srcDates.indexOf(l.date);
      if (di === -1) continue;
      await axios.post('/api/lessons', {
        subjectId: l.subjectId, topic: l.topic, materials: l.materials,
        duration: l.duration || 1, date: tgtDates[di],
      }, { headers: authHdr });
    }
    setShowCopyModal(false);
    fetchAllLessons(); fetchWeekLessons(selectedWeek);
  }

  const weekDates  = getWeekDates(selectedWeek);
  const teacher    = JSON.parse(localStorage.getItem('user') || 'null');

  // Ghost preview from own recurring schedule (rendered only when the week is empty)
  const weekGhosts = scheduleEntries.map(e => ({
    id: 'g-' + e.id, dayIndex: e.dayIndex, slotIndex: e.slotIndex,
    duration: e.duration || 1, subjectId: e.subjectId, classId: e.classId,
  }));

  const navItems = [
    ['woche',          'Wochenraster',  Grid3x3Gap],
    ['lektionen',      'Lektionen',     Journal],
    ['stundenplan',    'Stundenplan',   CalendarWeek],
    ['einstellungen',  'Einstellungen', GearFill],
  ];

  const routeTitles = {
    woche:         'Wochenraster',
    lektionen:     'Lektionen',
    stundenplan:   'Stundenplan',
    einstellungen: 'Einstellungen',
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px 1fr', height: 'calc(100vh - 57px)', overflow: 'hidden', background: 'var(--bg)' }}>

      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside style={{
        background: 'var(--surface)', borderRight: '1px solid var(--line)',
        padding: '20px 14px', display: 'flex', flexDirection: 'column', gap: 2,
        overflowY: 'auto',
        ...(isMobile ? {
          position: 'fixed', top: 57, left: 0, bottom: 0, width: 220, zIndex: 200,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
          boxShadow: sidebarOpen ? 'var(--sh-3)' : 'none',
        } : {}),
      }}>
        {navItems.map(([k, label, Icon]) => (
          <button key={k} className="sidebar-nav-btn" onClick={() => { setRoute(k); if (isMobile) setSidebarOpen(false); }} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
            borderRadius: 'var(--r-s)', border: 'none', cursor: 'pointer',
            textAlign: 'left', fontFamily: 'var(--font-sans)', fontSize: 13.5,
            fontWeight: 500, width: '100%',
            background: route === k ? 'var(--paper)' : 'transparent',
            color:      route === k ? 'var(--ink-1)' : 'var(--ink-2)',
            boxShadow:  route === k ? 'var(--sh-1)'  : 'none',
            transition: 'background 0.15s, color 0.15s',
          }}>
            <Icon size={15} /> {label}
          </button>
        ))}

        {/* User footer */}
        {teacher && (
          <div style={{ marginTop: 'auto', borderTop: '1px solid var(--line)', paddingTop: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 10px 0' }}>
            <div className="avatar" style={{ flexShrink: 0 }}>{initials(teacher.name || teacher.username || '?')}</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-1)' }}>{teacher.name}</div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>Lehrer</div>
            </div>
          </div>
        )}
      </aside>

      {/* ── Main ────────────────────────────────────────────────────────── */}
      <main style={{ padding: isMobile ? '16px 16px 60px' : '28px 36px 60px', overflowY: 'auto' }}>

        {/* Header */}
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            {isMobile && (
              <button onClick={() => setSidebarOpen(o => !o)} style={{
                marginTop: 2, padding: '6px 8px', borderRadius: 'var(--r-s)',
                border: '1px solid var(--line)', background: 'transparent',
                color: 'var(--ink-2)', cursor: 'pointer', display: 'flex', alignItems: 'center',
              }}>
                <MenuIcon size={18} />
              </button>
            )}
          <div>
            <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{routeTitles[route]}</h1>
          </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button data-no-print className="btn" onClick={() => setShowCopyModal(true)} title="Woche kopieren" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-s)', background: 'transparent', color: 'var(--ink-2)', cursor: 'pointer', fontSize: 13 }}>
              <Copy size={14} /> KW kopieren
            </button>
            {(route === 'woche' || route === 'lektionen') && (
              <button className="btn btn-grad" onClick={() => openCreateLesson(weekDates[0])} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Plus size={16} /> Neue Lektion
              </button>
            )}
          </div>
        </header>

        {/* Views */}
        {route === 'woche' && (
          <WochenrasterView
            weekLessons={weekLessons} weekDates={weekDates} subjects={subjects} classes={classes}
            maxLessons={maxLessons} lessonTimes={lessonTimes} ghosts={weekGhosts}
            onEditLesson={openEditLesson}
            onCreateLesson={openCreateLesson}
            onMoveLesson={handleMoveLesson}
          />
        )}
        {route === 'stundenplan' && (
          <ScheduleEditor
            subjects={subjects} classes={classes} maxLessons={maxLessons} lessonTimes={lessonTimes}
            authHdr={authHdr} teacherId={teacher?.id} isMobile={isMobile}
          />
        )}
        {route === 'lektionen' && (
          <LektionenView
            allLessons={allLessons} weekLessons={weekLessons} subjects={subjects} classes={classes}
            lessonTimes={lessonTimes}
            onEdit={openEditLesson} onDelete={handleDeleteLesson}
          />
        )}
        {route === 'einstellungen' && (
          <div className="card-flat" style={{ padding: 28, maxWidth: 480 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Stundenplan</div>
            <h2 className="display" style={{ fontSize: 22, margin: '0 0 20px' }}>Lektionszeiten</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: maxLessons }, (_, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '36px 1fr 16px 1fr', gap: 8, alignItems: 'center' }}>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>L{i + 1}</div>
                  <input className="input" type="time"
                    value={lessonTimes[i]?.start || ''}
                    onChange={e => {
                      const updated = [...lessonTimes];
                      updated[i] = { ...updated[i], start: e.target.value };
                      setLessonTimes(updated);
                    }} />
                  <div style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>–</div>
                  <input className="input" type="time"
                    value={lessonTimes[i]?.end || ''}
                    onChange={e => {
                      const updated = [...lessonTimes];
                      updated[i] = { ...updated[i], end: e.target.value };
                      setLessonTimes(updated);
                    }} />
                </div>
              ))}
            </div>
            <button
              className="btn btn-grad"
              style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={() => axios.put('/api/settings', { lessonTimes }, { headers: authHdr })}
            >
              Speichern
            </button>
          </div>
        )}
        {route === 'einstellungen' && (
          <div className="card-flat" style={{ padding: 28, maxWidth: 480, marginTop: 20 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Design</div>
            <h2 className="display" style={{ fontSize: 22, margin: '0 0 20px' }}>Fächerfarben</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {subjects.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
                  <span style={{ fontSize: 13 }}>{s.name}</span>
                  <input
                    type="color"
                    value={s.color || '#7287fd'}
                    onChange={e => {
                      const color = e.target.value;
                      axios.put(`/api/admin/subjects/${s.id}`, { color }, { headers: authHdr }).catch(console.error);
                      setSubjects(prev => prev.map(sub => sub.id === s.id ? { ...sub, color } : sub));
                    }}
                    style={{ width: 40, height: 28, padding: 0, border: '1px solid var(--line)', borderRadius: 4, background: 'none', cursor: 'pointer' }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* ── Lesson Modal ────────────────────────────────────────────────── */}
      <Modal show={showLessonModal} onHide={() => { setShowLessonModal(false); setEditingLesson(null); setSaveAsTemplate(false); setTemplateName(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingLesson ? 'Lektion bearbeiten' : 'Neue Lektion'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Datum</Form.Label>
              <Form.Control type="date" value={lessonDate} onChange={e => setLessonDate(e.target.value)} />
            </Form.Group>
            {templates.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Aus Vorlage laden</Form.Label>
                <Form.Select defaultValue="_none" onChange={e => {
                  const tpl = templates.find(t => t.id === e.target.value);
                  if (tpl) setLessonForm(f => ({ ...f, subjectId: tpl.subjectId, topic: tpl.topic, materials: tpl.materials || '' }));
                  e.target.value = '_none';
                }}>
                  <option value="_none">— Vorlage wählen —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Fach</Form.Label>
              <Form.Select value={lessonForm.subjectId} onChange={e => setLessonForm({ ...lessonForm, subjectId: e.target.value })}>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Klasse</Form.Label>
              <Form.Select value={lessonForm.classId} onChange={e => setLessonForm({ ...lessonForm, classId: e.target.value })}>
                <option value="">— Alle Klassen —</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Thema</Form.Label>
              <Form.Control type="text" value={lessonForm.topic}
                onChange={e => setLessonForm({ ...lessonForm, topic: e.target.value })} placeholder="Lektionsthema" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Materialien</Form.Label>
              <Form.Control as="textarea" rows={3} value={lessonForm.materials}
                onChange={e => setLessonForm({ ...lessonForm, materials: e.target.value })} placeholder="Materialien (optional)" />
              <Form.Text className="text-muted">Markdown wird unterstützt</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dauer</Form.Label>
              <Form.Select value={lessonForm.duration} onChange={e => setLessonForm({ ...lessonForm, duration: parseInt(e.target.value) })}>
                <option value={1}>1 Lektion</option>
                <option value={2}>2 Lektionen (Doppelstunde)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Check type="checkbox" label="Als Vorlage speichern"
                checked={saveAsTemplate}
                onChange={e => { setSaveAsTemplate(e.target.checked); if (!templateName) setTemplateName(lessonForm.topic); }}
              />
              {saveAsTemplate && (
                <Form.Control className="mt-2" type="text" value={templateName}
                  onChange={e => setTemplateName(e.target.value)} placeholder="Vorlagenname" />
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {editingLesson && (
            <Button variant="danger" className="me-auto"
              onClick={() => { handleDeleteLesson(editingLesson.id); setShowLessonModal(false); setEditingLesson(null); }}>
              Löschen
            </Button>
          )}
          <Button variant="secondary" onClick={() => { setShowLessonModal(false); setEditingLesson(null); setSaveAsTemplate(false); setTemplateName(''); }}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSaveLesson}>Speichern</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Copy Week Modal ─────────────────────────────────────────────── */}
      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} centered size="sm">
        <Modal.Header closeButton><Modal.Title>Woche kopieren</Modal.Title></Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 16 }}>
            Alle Lektionen von KW {selectedWeek} in eine andere Woche kopieren.
          </p>
          <Form.Group>
            <Form.Label>Zielwoche (KW)</Form.Label>
            <Form.Control type="number" min={1} max={52} value={copyTargetWeek}
              onChange={e => setCopyTargetWeek(parseInt(e.target.value) || 1)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleCopyWeek}>Kopieren</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TeacherDashboardB;
